import {Box, P, showMessage} from 'exsportia-components'
import {DateTime} from 'luxon'
import React, {FC, memo, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import {SUBSCRIPTION_DURATION_TYPE_MAP} from '../../api/subscriptions/constants'
import {useCreateSubscriptionRequest, useSubscription} from '../../api/subscriptions/hooks'
import {SubscriptionData} from '../../api/subscriptions/types'
import {useVenue} from '../../api/venue/hooks'
import {Amenities} from '../../components/amenities/amenities'
import {SubscriptionPreviewCard} from '../../components/subscription-preview-card/subscription-preview-card'
import {uidMaker} from '../../helpers/logic'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {TranslatorScope} from '../../locale/types'
import {useAuthState} from '../../services/auth/hooks'
import {useCheckSubscriptionPasscode} from '../../services/booking/use-check-subscription-passcode'
import {useOnlinePayability} from '../../services/payments/hooks/use-online-payability'
import {Button} from '../../ui/components/button/button'
import {DividerWithContent} from '../../ui/components/divider-with-content/divider-with-content'
import {GradientLoader} from '../../ui/components/gradient-loader/gradient-loader'
import {LabeledValueRow} from '../../ui/components/key-value-row/labeled-value-row'
import {TextWithShowMore} from '../../ui/components/text-with-show-more/text-with-show-more'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {getStepAfterSubscriptionProfile, hasFormForEntities} from '../booking-form/form-step'
import {ContactUsPage} from '../contact-us/contact-us-page'
import {BenefitsList} from './benefits/benefits-list'
import {calculateSubscriptionStartDate} from './helpers'
import {PaymentOptionsList} from './payment/payment-options-list'

const Header: FC = memo(() => {
  const {l} = useLocale()
  const {data: venue} = useVenue()

  return <SimpleGridLayoutHeader title={venue?.goLive === false ? l('venueUnpublished') : l('subscription')} />
})

const requestStatusesLabels = {
  ACCEPT: 'allowed',
  DEFAULT: 'sendRequest',
  DECLINE: 'your_request_declined',
  DECLINE_BY_USER: 'your_request_declined',
  DECLINE_BY_VENUE: 'your_request_declined',
  AWAITING_VENUE_AUTHORISATION: 'yourRequestHasBeenSent',
  AWAITING_MEMBER_AUTHORISATION: 'yourRequestHasBeenSent',
}

const buttonDisabledStatuses: (string | null | undefined)[] = [
  'DECLINE_BY_USER',
  'DECLINE_BY_VENUE',
  'DECLINE_BY_MEMBER',
  'AWAITING_VENUE_AUTHORISATION',
  'AWAITING_MEMBER_AUTHORISATION',
]

const RequestButton: FC<{requestStatus: SubscriptionData['requestStatus']; isLoading: boolean}> = ({
  requestStatus,
  isLoading,
}) => {
  const {subscriptionId, venueId} = useParams<{subscriptionId: string; venueId: string}>()
  const {l} = useLocale()
  const {refetch} = useSubscription(subscriptionId)
  const {
    mutate: createRequest,
    isLoading: isRequestLoading,
    isSuccess,
  } = useCreateSubscriptionRequest(subscriptionId || '')
  const {isLoggedIn, signIn} = useAuthState()
  const handleOnClick = useCallback(() => {
    if (!isLoggedIn) {
      return signIn()
    }
    createRequest({
      data: {},
      target: 'MEMBERSHIP',
      venueId,
      invitationId: uidMaker(30),
    })
  }, [isLoggedIn, subscriptionId, venueId, createRequest])
  useEffect(() => {
    if (isSuccess) {
      refetch()
    }
  }, [isSuccess])
  return (
    <Button
      type='secondary'
      disabled={isLoading || isRequestLoading || buttonDisabledStatuses.includes(requestStatus)}
      isLoading={isLoading || isRequestLoading}
      onClick={handleOnClick}
    >
      {l(requestStatusesLabels[requestStatus || 'DEFAULT'] as TranslatorScope)}
    </Button>
  )
}

export const SubscriptionProfilePage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {subscriptionId} = useParams<{subscriptionId: string}>()
  const {isLoggedIn, signUp} = useAuthState()
  const {data: subscription, isFetching: isSubscriptionLoading, refetch: refetchSubscription} = useSubscription()
  const {data: venue, isFetching: isVenueLoading, refetch: refetchVenue} = useVenue()
  const {openModal, closeModal} = useModalActions()
  const {openSubscriptionPasscodeCheckModal: checkPasscode} = useCheckSubscriptionPasscode()
  const [isPasscodeVerified, setIsPasscodeVerified] = useState(false)
  const amenities = (subscription?.amenitiesDiffer ? subscription.amenities : venue?.amenities) || []
  const startDate = useMemo(() => {
    if (!subscription) {
      return null
    }
    if (subscription.startDateType === 'USER_SELECTED_DATE') {
      return l('userSelect' as TranslatorScope)
    }
    let dateTimeStartDate: DateTime<boolean>
    if (typeof subscription.startDate === 'string') {
      console.log('subscription.startDate', subscription.startDate)
      dateTimeStartDate = DateTime.fromFormat(subscription.startDate, 'd/M/yyyy') as DateTime<boolean>
    } else if (typeof subscription.startDate === 'number') {
      dateTimeStartDate = DateTime.fromMillis(subscription.startDate) as DateTime<boolean>
    } else {
      dateTimeStartDate = subscription.startDate
    }
    const calculatedStartDate = calculateSubscriptionStartDate({
      rolling: subscription.rolling,
      startDateType: subscription.startDateType,
      fromDateOfPurchaseAfterStartDate: subscription.fromDateOfPurchaseAfterStartDate,
      startDate: dateTimeStartDate,
    })

    return calculatedStartDate.toLocaleString(DateTime.DATE_MED)
  }, [subscription])

  const isRollingSub = subscription?.rolling ?? false
  const duration = useMemo(() => {
    if (!subscription) {
      return null
    }
    return SUBSCRIPTION_DURATION_TYPE_MAP[subscription.durationType].label
  }, [subscription])
  const goLiveFlag = useMemo(() => {
    const subGoLiveValue: unknown = subscription ? (subscription as unknown as {goLive?: unknown}).goLive : undefined
    const venueGoLiveValue: unknown = venue ? (venue as unknown as {goLive?: unknown}).goLive : undefined

    if (venueGoLiveValue === false) {
      return false
    }

    return typeof subGoLiveValue === 'boolean' ? subGoLiveValue : true
  }, [subscription, venue])

  const isNotAvailableToPurchase = useMemo(() => {
    const reasonNotAvailable = subscription?.permissionData.reason === 'NOT_AVAILABLE'
    return Boolean(reasonNotAvailable || !goLiveFlag)
  }, [subscription, goLiveFlag])

  const needRequest = useMemo(
    () => subscription?.byRequestOnly && subscription.requestStatus !== 'ACCEPT' && goLiveFlag,
    [subscription, goLiveFlag],
  )

  const isValidated = useMemo(() => subscription?.permissionData.validated, [subscription])

  // The venue has no terminal that could take the money, so the pay step could only dead-end.
  // The membership itself stays on sale — name, description, benefits and price all remain — but
  // the CTA becomes honest instead of failing at the last step. A group venue is the case that
  // has this today; the state is not group-specific.
  const {hasNoPaymentTerminal} = useOnlinePayability()
  const isOnlinePurchaseUnavailable = useMemo(
    () => hasNoPaymentTerminal && !isNotAvailableToPurchase && !needRequest,
    [hasNoPaymentTerminal, isNotAvailableToPurchase, needRequest],
  )

  const [isCheckingForm, setIsCheckingForm] = useState(false)
  const isNavigateLoading = isVenueLoading || isSubscriptionLoading || isCheckingForm || !venue || !subscription

  // Look the form up while the button spins, so a subscription without a form goes straight to
  // the payment step instead of through an empty Form step.
  const navigateToNextStep = useCallback(
    (subscriptionUid: string) => {
      setIsCheckingForm(true)
      hasFormForEntities('subscription', [subscriptionUid])
        .then((hasForm) => navigate(getStepAfterSubscriptionProfile(hasForm)))
        .finally(() => setIsCheckingForm(false))
    },
    [navigate],
  )

  const navigateToPay = useCallback(() => {
    if (!isLoggedIn) {
      signUp()
      return
    }
    Promise.all([refetchSubscription(), refetchVenue()])
      .then(([subscriptionResult]) => {
        const updatedSubscription = subscriptionResult.data
        if (!updatedSubscription) {
          showMessage(l('somethingWentWrong'), 'error')
          return
        }
        const updatedIsValidated = updatedSubscription.permissionData.validated
        const updatedNeedRequest =
          updatedSubscription.byRequestOnly && updatedSubscription.requestStatus !== 'ACCEPT'
        if (!updatedIsValidated || updatedNeedRequest) {
          showMessage(l('permissions_were_changed', {caseAction: 'capitalize'}), 'error')
          return
        }
        navigateToNextStep(updatedSubscription.uid)
      })
      .catch(() => {
        showMessage(l('permissions_were_changed', {caseAction: 'capitalize'}), 'error')
      })
  }, [isLoggedIn, refetchSubscription, refetchVenue, navigateToNextStep, signUp, l])

  const openUnavailablePurchaseModal = useCallback(() => {
    openModal({
      title: venue?.goLive === false ? l('venueUnpublished') : l('unavailable'),
      component: (
        <Box>
          <P>{venue?.goLive === false ? l('venueUnpublishedDesc') : l('subscriptionUnavailableDesc')}</P>
        </Box>
      ),
      settings: {
        footer: {
          submitButton: {
            settings: {
              width: 'max-content',
            },
          },
        },
      },
      footerConfig: {
        submitButton: {
          text: l('contactUs'),
          onClick: () => {
            openModal({
              title: l('contactUs'),
              // eslint-disable-next-line
              component: <ContactUsPage venueId={(venue && venue.uid) || ''} />,
              footerConfig: {disabled: true},
            })
          },
        },
        cancelButton: {
          text: l('home'),
          onClick: () => {
            navigate(`/${venue?.uid || ''}`)
            closeModal()
          },
        },
      },
    })
  }, [openModal, closeModal, l, venue, navigate])

  const openSubscriptionPasscodeCheckModal = useCallback(() => {
    if (!subscriptionId) {
      return
    }
    checkPasscode({
      subscriptionId,
      onSuccess: () => {
        setIsPasscodeVerified(true)
        navigateToNextStep(subscriptionId)
      },
    })
  }, [checkPasscode, navigateToNextStep, subscriptionId])

  useEffect(() => {
    if (subscription && !subscription.permissionData.show && !needRequest && !isNotAvailableToPurchase) {
      navigate('../')
    }
  }, [subscription, needRequest, isNotAvailableToPurchase, navigate])

  return (
    <GridLayout header={<Header />}>
      <Box>
        <Box mb={'16px'}>
          <P
            fontWeight={'bold'}
            fontSize={['h1', 'desktop-h1']}
          >
            {subscription?.name || <GradientLoader style={{width: '150px'}} />}
          </P>
        </Box>
        {subscription && (
          <Box>
            {Boolean(subscription.description) && (
              <Box mb={'16px'}>
                <TextWithShowMore text={`${subscription.description}`} />
              </Box>
            )}
            {isNotAvailableToPurchase && (
              <TitledSection title={venue?.goLive === false ? l('venueUnpublished') : l('subscriptionUnavailable')}>
                <P fontSize={'body'}>{venue?.goLive === false ? l('venueUnpublishedDesc') : l('subscriptionUnavailableDesc')}</P>
              </TitledSection>
            )}
            {Boolean(amenities.length) && (
              <TitledSection title={l('facilities')}>
                <Amenities amenities={amenities} />
              </TitledSection>
            )}
            {Boolean(subscription.termsAndConditions) && (
              <TitledSection title={l('termsConditions')}>
                <P fontSize={'body'}>{subscription.termsAndConditions}</P>
              </TitledSection>
            )}
          </Box>
        )}
        <BenefitsList subscriptionId={subscription?.uid} />
      </Box>
      <Box>
        <SubscriptionPreviewCard id={subscription?.uid} />
        <Box mt='24px'>
          {subscription?.uid ? (
            <Box mt='24px'>
              <PaymentOptionsList subscriptionId={subscription.uid} />
            </Box>
          ) : (
            <GradientLoader />
          )}
        </Box>
        <DividerWithContent />
        <LabeledValueRow
          label={l('startDate')}
          align={'flex-start'}
        >
          {startDate || <GradientLoader style={{width: '75px'}} />}
        </LabeledValueRow>
        <DividerWithContent />
        <LabeledValueRow label={l('duration')}>
          {subscription ? (
            isRollingSub ? (
              l('monthly')
            ) : duration ? (
              `${subscription.duration} ${l(duration as TranslatorScope)}`
            ) : (
              <GradientLoader style={{width: '75px'}} />
            )
          ) : (
            <GradientLoader style={{width: '75px'}} />
          )}
        </LabeledValueRow>
        <Box mt={'16px'}>
          {needRequest ? (
            <RequestButton
              isLoading={isNavigateLoading}
              requestStatus={subscription?.requestStatus}
            />
          ) : (
            <Box
              title={
                !isNavigateLoading && isNotAvailableToPurchase ? l('subscriptionUnavailableDesc') : undefined
              }
              style={
                !isNavigateLoading && isNotAvailableToPurchase ? {opacity: 0.5, cursor: 'not-allowed'} : undefined
              }
            >
              <Button
                type={isNotAvailableToPurchase ? 'secondary' : 'primary'}
                onClick={() => {
                  if (isNavigateLoading || isOnlinePurchaseUnavailable) {
                    return
                  }
                  if (isNotAvailableToPurchase) {
                    openUnavailablePurchaseModal()
                    return
                  }
                  const isPrivate = Boolean((subscription as { private?: unknown }).private)
                  if (isPrivate && !isPasscodeVerified) {
                    openSubscriptionPasscodeCheckModal()
                    return
                  }
                  navigateToPay()
                }}
                disabled={
                  isNavigateLoading || isOnlinePurchaseUnavailable || (!isValidated && !isNotAvailableToPurchase)
                }
                isLoading={isNavigateLoading}
              >
                {l('get_subscription', {caseAction: 'capitalize'})}
              </Button>
              {isOnlinePurchaseUnavailable && (
                <Box mt={'8px'}>
                  <P
                    fontSize={'body'}
                    color={'text-secondary'}
                  >
                    {l('onlinePurchaseUnavailableDesc')}
                  </P>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </GridLayout>
  )
}
